import React, { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    Avatar,
    Typography,
    Box,
    Pagination,
    Tooltip,
    IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { GetItem } from "utils/storage";
import { DefaultSwrOptions, StorageKey } from "../../../constants/constants";
import { GetContact } from "../../../service/odoo";

const ContactSearchPage = ({ onSelectContact, navigate }) => {
    const [contactResults, setContactResults] = useState([]);
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: 10,
        total_records: 0,
    });
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [showAdditionalFields, setShowAdditionalFields] = useState(false);
    const [error, setError] = useState("");
    const [lastSearchParams, setLastSearchParams] = useState({}); // Para guardar los últimos parámetros de búsqueda
    const [maritalStatus, setMaritalStatus] = useState([]);

    useEffect(() => {
        fetchMaritalStatus();
    }, []);

    const fetchMaritalStatus = async () => {
        const user = await GetItem(StorageKey.USER);
        setMaritalStatus(user.metadata.marital_status);
    };

    const getSpanishTranslation = (value) => {
        let data = maritalStatus;
        const found = data.find((item) => item.value === value);
        return found ? found.spanish : "";
    };

    const searchValidationSchema = Yup.object({
        first_name: Yup.string(),
        middle_name: Yup.string(),
        last_name: Yup.string(),
        phone: Yup.string()
    });


    const validateQueryParams = (params) => {
        const filteredParams = Object.keys(params)
            .filter(key => key !== "limit" && key !== "offset") // Excluir limit y offset
            .reduce((obj, key) => {
                obj[key] = params[key];
                return obj;
            }, {});

        const hasValues = Object.keys(filteredParams).some(key => filteredParams[key]);
        return hasValues;
    };

    const buildQueryParams = (params) => {
        return Object.keys(params)
            .filter(key => params[key]) // Solo incluir campos con valores
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join("&");
    };



    const fetchContacts = async (queryParams) => {
        if (!validateQueryParams(queryParams)) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Debes completar al menos un campo para realizar la búsqueda.",
                timer: 2000,
            });
            return false;
        }

        try {
            setLoadingSubmit(true);
            const finalQueryParams = buildQueryParams(queryParams);
            const res = await GetContact(finalQueryParams);

            if (res.status === 200 && res.data.data.length > 0) {
                return res.data;
            }

            return null;
        } catch (err) {
            setError("Error al buscar el contacto.");
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const handleSearchContact = async (values) => {
        setPagination((prev) => ({ ...prev, offset: 0 })); // Reinicia la paginación
        const queryParams = {
            ...values,
            limit: pagination.limit,
            offset: 0,
        };
        setLastSearchParams(queryParams); // Guarda los parámetros actuales de búsqueda

        const result = await fetchContacts(queryParams);

        if (result) {
            setContactResults(result.data);
            setPagination(result.pagination);
            setShowAdditionalFields(false);
        } else if (result === null) {
            setError("No se encontraron contactos.");
            setShowAdditionalFields(true);
        }
    };

    const handlePageChange = async (event, page) => {
        const newOffset = (page - 1) * pagination.limit;

        const queryParams = {
            ...lastSearchParams,
            offset: newOffset,
            limit: pagination.limit,
        };

        const result = await fetchContacts(queryParams);

        if (result) {
            setContactResults(result.data);
            setPagination(result.pagination);
            setError("");
        } else {
            setContactResults([]); // Limpia resultados previos
            setError("No se encontraron contactos.");
            setShowAdditionalFields(true);
        }
    };

    const handleSelectContact = (contact) => {
        onSelectContact(contact);
    };

    const totalPages = Math.ceil(pagination.total_records / pagination.limit);
    const getRandomColor = (index) => {
        const colors = [
            "#FFC1E3", // Rosa pastel
            "#FFDDC1", // Durazno claro
            "#FFF1C1", // Amarillo pálido
            "#C1FFD7", // Verde menta
            "#C1E7FF", // Azul cielo
            "#D1C1FF", // Lila pastel
            "#FFC1C1", // Rosa pálido
            "#F7D6C1", // Arena
            "#D6EBC1", // Verde lima claro
            "#C1F3FF", // Turquesa suave
        ];
        return colors[index % colors.length]; // Selección cíclica para que no se repitan
    };


    return (
        <div>
            <h1>Búsqueda de Contactos</h1>
            <Formik
                initialValues={{
                    email: "",
                    first_name: "",
                    middle_name: "",
                    last_name: "",
                    phone: "",
                }}
                validationSchema={searchValidationSchema}
                onSubmit={handleSearchContact}
            >
                {({ errors, touched }) => (
                    <Form>
                        <Grid container spacing={2}>


                            <Grid item xs={12} sm={6}>


                                <Field name="first_name">
                                    {({ field }) => (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                                            <TextField
                                                {...field}
                                                label="Nombre(s)"
                                                fullWidth
                                                variant="outlined"
                                                error={touched.first_name && !!errors.first_name}
                                                helperText={touched.first_name && errors.first_name}
                                            />

                                        </div>
                                    )}
                                </Field>
                                <Box display="flex" alignItems="center" mt={1}>

                                    <Tooltip title="Introduzca los primeros nombres para realizar una busqueda.." placement="right">
                                        <InfoOutlinedIcon
                                            sx={{
                                                fontSize: "1rem",
                                                color: "red",
                                                marginLeft: "4px",
                                                cursor: "pointer",
                                            }}
                                        />
                                    </Tooltip>
                                    <Typography variant="caption" color="textSecondary">
                                        Introduzca los primeros nombres para realizar una búsqueda..
                                    </Typography>
                                </Box>
                            </Grid>


                            {/* <Grid item xs={12} sm={6}>
                                <Field name="middle_name">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Apellido Paterno"
                                            fullWidth
                                            variant="outlined"
                                            error={touched.middle_name && !!errors.middle_name}
                                            helperText={touched.middle_name && errors.middle_name}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Field name="last_name">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Apellido Materno"
                                            fullWidth
                                            variant="outlined"
                                            error={touched.last_name && !!errors.last_name}
                                            helperText={touched.last_name && errors.last_name}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Field name="email">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Correo Electrónico"
                                            fullWidth
                                            variant="outlined"
                                            error={touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                        />
                                    )}
                                </Field>
                            </Grid> */}
                            <Grid item xs={12} sm={6}>

                                <Field name="phone">
                                    {({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Teléfono"
                                            fullWidth
                                            variant="outlined"
                                            error={touched.phone && !!errors.phone}
                                            helperText={touched.phone && errors.phone}
                                        />
                                    )}
                                </Field>

                                <Box display="flex" alignItems="center" mt={1}>

                                    <Tooltip title="Introduzca los primeros 5 0 6 digitos.." placement="right">
                                        <InfoOutlinedIcon
                                            sx={{
                                                fontSize: "1rem",
                                                color: "red",
                                                marginLeft: "4px",
                                                cursor: "pointer",
                                            }}
                                        />
                                    </Tooltip>
                                    <Typography variant="caption" color="textSecondary">
                                        Introduzca los primeros 5 o 6 dígitos...
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" gap={2}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SearchIcon />}
                                    >
                                        Buscar
                                    </Button>
                                    {showAdditionalFields || contactResults.length > 0 && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<PersonAddIcon />}
                                            onClick={() => onSelectContact({})}
                                        >
                                            Crear Nuevo Contacto
                                        </Button>)}
                                </Box>
                            </Grid>

                        </Grid>
                    </Form>
                )}
            </Formik>
            {contactResults.length > 0 && (
                <Box mt={4} display="flex" justifyContent="center">
                    <Pagination
                        count={totalPages}
                        color="primary"
                        onChange={handlePageChange}
                        page={Math.ceil(pagination.offset / pagination.limit) + 1}
                    />
                </Box>
            )}
            <Box mt={4}>
                {contactResults.length > 0 && (
                    <Grid container spacing={2}>
                        {contactResults.map((contact) => (
                            <Grid item xs={12} sm={6} md={4} key={contact.id}>
                                <Card
                                    onClick={() => handleSelectContact(contact)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <CardContent
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", sm: "row" },
                                            alignItems: { xs: "center", sm: "flex-start" },
                                            gap: 2,
                                        }}
                                    >
                                        <Avatar
                                            src={contact.image_url || "/default-image.jpg"}
                                            variant="square" // Esto hace que el Avatar sea cuadrado
                                            sx={{
                                                width: { xs: 140, sm: 120 }, // Tamaño más grande para pantallas pequeñas
                                                height: { xs: 140, sm: 120 },
                                                marginBottom: { xs: 2, sm: 0 },
                                                borderRadius: 3, // Hace que las esquinas sean ligeramente redondeadas
                                            }}
                                        />
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                {contact.first_name +
                                                    " " +
                                                    contact.middle_name +
                                                    " " +
                                                    contact.last_name || ""}
                                            </Typography>
                                            <ul style={{ paddingLeft: "20px", margin: 0 }}>
                                                <li>Iglesia: {contact.church?.name || ""}</li>

                                                <li>Celular: {contact.mobile || ""}</li>
                                                <li>Correo: {contact.email || ""}</li>
                                                <li>Estado civil: {getSpanishTranslation(contact.marital_status) || ""}</li>
                                                <li>Edad: {contact.age || ""}</li>
                                                <li>
                                                    Grupos:
                                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                                        {contact.groups.map((group) => (
                                                            <Box
                                                                key={group.id}
                                                                sx={{
                                                                    backgroundColor: getRandomColor(group.id),
                                                                    color: "#000",
                                                                    borderRadius: "16px",
                                                                    padding: "4px 12px",
                                                                    fontSize: "14px",
                                                                    fontWeight: "bold",
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                }}
                                                            >
                                                                {group.name}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </li>
                                            </ul>
                                        </Box>
                                    </CardContent>


                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
            {contactResults.length > 0 && (
                <Box mt={4} display="flex" justifyContent="center">
                    <Pagination
                        count={totalPages}
                        color="primary"
                        onChange={handlePageChange}
                        page={Math.ceil(pagination.offset / pagination.limit) + 1}
                    />
                </Box>
            )}



            {showAdditionalFields && (
                <Box mt={4} display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="body1" color="textSecondary" mb={2}>
                        No se encontraron contactos. Puede crear uno nuevo.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onSelectContact({})}
                    >
                        Crear Nuevo Contacto
                    </Button>
                </Box>
            )}

        </div>
    );
};

export default ContactSearchPage;

